import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import { UserProfileDetailsHeader } from '../userProfileDetails';
import { SecuredComponent } from '@/components/hoc';
import { AdminUserPermissionsEnum } from '@/lib/enum';
import { BadgeCounter } from '@/components/custom';
import { useUrlParams } from './hooks';
import { TProps, TTabname } from '.';
import ProfileDetailUserDataTransactions from './profileDetailUserDataTransactions';
import ProfileDetailUserDataSubAccounts from './profileDetailUserDataSubAccounts';
import ProfileDetailUserDataPayments from './profileDetailUserDataPayments';
import ProfileDetailUserDataPushes from './profileDetailUserDataPushes';
import ProfileDetailUserDataPhotos from './ProfileDetailUserDataPhotos';
import ProfileDetailUserDataBio from './ProfileDetailUserDataBio';
import { cn } from '@/lib/utils';
import { useRequestGetUserDetailsTabsStats } from '@/hooks/httpRequest';
import { useProfileUserStore } from './store/profileUserStore';
import { useEffect } from 'react';

const ProfileDetailUserData = ({ userData }: TProps) => {
	const { tab, setValue, setValues } = useUrlParams();
	const userId = userData?._id;
	const { setUserData } = useProfileUserStore();

	// Keep the store in sync with the prop
	useEffect(() => {
		setUserData(userData);
		return () => setUserData(null);
	}, [userData, setUserData]);

	// Fetch counts independently for badges via the new stats endpoint
	const { stats, isLoadingStats } = useRequestGetUserDetailsTabsStats(
		{ userId: userId! },
		!!userId
	);
	// Default to photos if no tab is selected
	const currentTab = (tab as string) || 'photos';

	const transactionParams = { limit: null, page: null, sortBy: null, sortType: null };

	const handleTabChange = (value: string) => {
		if (value !== 'transactions') {
			setValues({ tab: value as TTabname, ...transactionParams });
		} else {
			setValue('tab', value as TTabname);
		}
	};

	if (!userId) {
		return <div>User ID missing</div>;
	}

	return (
		<div className='flex flex-col space-y-3 animate-in fade-in duration-500'>
			<div className='flex flex-col space-y-2'>
				<UserProfileDetailsHeader
					hideSeparator={!(
						userData?.verifications?.bio ||
						userData.verifications?.id?.photo ||
						(userData.pictures?.length ?? 0) !== 0)
					}
					userData={userData} />
				<ProfileDetailUserDataBio userData={userData} />
			</div>

			<Tabs value={currentTab} onValueChange={handleTabChange} className="w-full">
				<TabsList className={cn(
					'w-full flex flex-nowrap overflow-x-auto overflow-y-hidden justify-start p-1 scrollbar-hide',
					'md:w-auto md:inline-flex md:justify-center'
				)}>
					<TabsTrigger value="photos" className="flex items-center space-x-2">
						<BadgeCounter
							isActive={currentTab === 'photos'}
							count={stats?.photosCount ?? 0}
							isLoading={isLoadingStats}
						/>
						<span>Photos</span>
					</TabsTrigger>

					<SecuredComponent permissionsRequired={[AdminUserPermissionsEnum.USERS_SUB_ACCOUNTS]}>
						<TabsTrigger value="subAccounts" className="flex items-center space-x-2">
							<BadgeCounter
								isActive={currentTab === 'subAccounts'}
								count={stats?.subaccountsCount ?? 0}
								isLoading={isLoadingStats}
							/>
							<span>Sous comptes</span>
						</TabsTrigger>
					</SecuredComponent>

					<SecuredComponent permissionsRequired={[AdminUserPermissionsEnum.USERS_TRANSACTIONS]}>
						<TabsTrigger value="transactions" className="flex items-center space-x-2">
							<BadgeCounter
								isActive={currentTab === 'transactions'}
								count={stats?.transactionsCount ?? 0}
								isLoading={isLoadingStats}
							/>
							<span>Transactions</span>
						</TabsTrigger>
					</SecuredComponent>

					<SecuredComponent permissionsRequired={[AdminUserPermissionsEnum.USERS_PAYMENTS]}>
						<TabsTrigger value="payments" className="flex items-center space-x-2">
							<BadgeCounter
								isActive={currentTab === 'payments'}
								count={stats?.paymentsCount ?? 0}
								isLoading={isLoadingStats}
							/>
							<span>Paiements</span>
						</TabsTrigger>
					</SecuredComponent>

					<SecuredComponent permissionsRequired={[AdminUserPermissionsEnum.USERS]}>
						<TabsTrigger value="pushes">
							Pushes
						</TabsTrigger>
					</SecuredComponent>
				</TabsList>

				<div className="mt-4">
					<TabsContent value="photos" className="m-0 focus-visible:outline-hidden focus-visible:ring-0">
						{currentTab === 'photos' && <ProfileDetailUserDataPhotos />}
					</TabsContent>

					<TabsContent value="subAccounts" className="m-0 focus-visible:outline-hidden focus-visible:ring-0">
						<SecuredComponent permissionsRequired={[AdminUserPermissionsEnum.USERS_SUB_ACCOUNTS]}>
							{currentTab === 'subAccounts' && <ProfileDetailUserDataSubAccounts userId={userId} />}
						</SecuredComponent>
					</TabsContent>

					<TabsContent value="transactions" className="m-0 focus-visible:outline-hidden focus-visible:ring-0">
						<SecuredComponent permissionsRequired={[AdminUserPermissionsEnum.USERS_TRANSACTIONS]}>
							{currentTab === 'transactions' && <ProfileDetailUserDataTransactions userId={userId} />}
						</SecuredComponent>
					</TabsContent>

					<TabsContent value="payments" className="m-0 focus-visible:outline-hidden focus-visible:ring-0">
						<SecuredComponent permissionsRequired={[AdminUserPermissionsEnum.USERS_PAYMENTS]}>
							{currentTab === 'payments' && <ProfileDetailUserDataPayments userId={userId} />}
						</SecuredComponent>
					</TabsContent>

					<TabsContent value="pushes" className="m-0 focus-visible:outline-hidden focus-visible:ring-0">
						<SecuredComponent permissionsRequired={[AdminUserPermissionsEnum.USERS]}>
							{currentTab === 'pushes' && <ProfileDetailUserDataPushes userId={userId} />}
						</SecuredComponent>
					</TabsContent>
				</div>
			</Tabs>
		</div>
	);
};

export default ProfileDetailUserData;
